import { FORM_TOKEN_REFRESH_AFTER_MS } from "@kmstories/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api.js";

export type FormId = "story" | "support" | "contact" | "newsletter" | "upload" | "share";

/**
 * The anti-spam time-trap token, kept fresh. Minted on mount; re-minted before
 * it can expire so a contributor who writes their life story slowly is never
 * rejected at submit. `getToken()` is what a submit handler calls — it returns
 * a token that is guaranteed current (or re-mints on the spot).
 */
export function useFormToken(formId: FormId) {
  const [token, setToken] = useState<string | null>(null);
  const mintedAt = useRef(0);
  const inFlight = useRef<Promise<string> | null>(null);

  const mint = useCallback((): Promise<string> => {
    if (inFlight.current) return inFlight.current;
    const p = api
      .post<{ token: string }>("/api/form-token", { formId })
      .then((r) => {
        mintedAt.current = Date.now();
        setToken(r.token);
        return r.token;
      })
      .finally(() => {
        inFlight.current = null;
      });
    inFlight.current = p;
    return p;
  }, [formId]);

  useEffect(() => {
    void mint().catch(() => {});
    const id = setInterval(() => {
      if (Date.now() - mintedAt.current > FORM_TOKEN_REFRESH_AFTER_MS) void mint().catch(() => {});
    }, 60_000);
    return () => clearInterval(id);
  }, [mint]);

  const getToken = useCallback(async (): Promise<string> => {
    if (token && Date.now() - mintedAt.current < FORM_TOKEN_REFRESH_AFTER_MS) return token;
    return mint();
  }, [mint, token]);

  return { token, getToken, remint: mint };
}
